/**
 * Sucursal Form Dialog - Create/Edit Branch
 *
 * Formulario para crear o editar sucursales del laboratorio.
 * Usa TanStack Query para las mutaciones (useCreateSucursal, useUpdateSucursal).
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import { useApiError } from "@/hooks/use-api-error";
import { useCreateSucursal, useUpdateSucursal, type Sucursal } from "@/hooks/use-sucursales";

interface SucursalFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sucursal: Sucursal | null;
  onSuccess: (isNew: boolean) => void;
}

// Schema de validación
const sucursalSchema = z.object({
  codigo: z.string().min(1, "sucursales.validation.codigoRequired"),
  nombre: z.string().min(2, "sucursales.validation.nombreRequired"),
  direccion: z.string().min(5, "sucursales.validation.direccionRequired"),
  telefono: z.string().min(8, "sucursales.validation.telefonoRequired"),
  email: z.string().email("sucursales.validation.emailInvalid").optional().or(z.literal("")),
  horario_apertura: z.string().optional(),
  horario_cierre: z.string().optional(),
  opera_24_horas: z.boolean(),
});

type FormData = z.infer<typeof sucursalSchema>;

export function SucursalFormDialog({
  open,
  onOpenChange,
  sucursal,
  onSuccess,
}: SucursalFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!sucursal;

  // TanStack Query mutations
  const createMutation = useCreateSucursal({
    onSuccess: () => onSuccess(true),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const updateMutation = useUpdateSucursal({
    onSuccess: () => onSuccess(false),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(sucursalSchema),
    defaultValues: {
      codigo: "",
      nombre: "",
      direccion: "",
      telefono: "",
      email: "",
      horario_apertura: "",
      horario_cierre: "",
      opera_24_horas: false,
    },
  });

  const opera24Horas = watch("opera_24_horas");

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (sucursal) {
        reset({
          codigo: sucursal.codigo,
          nombre: sucursal.nombre,
          direccion: sucursal.direccion,
          telefono: sucursal.telefono,
          email: sucursal.email || "",
          horario_apertura: sucursal.horario_apertura?.substring(0, 5) || "",
          horario_cierre: sucursal.horario_cierre?.substring(0, 5) || "",
          opera_24_horas: sucursal.opera_24_horas,
        });
      } else {
        reset({
          codigo: "",
          nombre: "",
          direccion: "",
          telefono: "",
          email: "",
          horario_apertura: "08:00",
          horario_cierre: "17:00",
          opera_24_horas: false,
        });
      }
      setError(null);
    }
  }, [open, sucursal, reset]);

  const onSubmit = (data: FormData) => {
    setError(null);

    const payload = {
      codigo: data.codigo,
      nombre: data.nombre,
      direccion: data.direccion,
      telefono: data.telefono,
      email: data.email || undefined,
      horario_apertura: data.opera_24_horas ? null : data.horario_apertura || null,
      horario_cierre: data.opera_24_horas ? null : data.horario_cierre || null,
      opera_24_horas: data.opera_24_horas,
    };

    if (isEditing && sucursal) {
      updateMutation.mutate({ id: sucursal.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t("sucursales.editSucursal") : t("sucursales.newSucursal")}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t("sucursales.editSucursalDesc") : t("sucursales.newSucursalDesc")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="grid gap-4">
            {/* Código y Nombre en una fila */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="codigo">{t("sucursales.fields.codigo")}</Label>
                <Input id="codigo" placeholder="SUC-001" {...register("codigo")} />
                {errors.codigo && (
                  <p className="text-sm text-destructive">{t(errors.codigo.message as string)}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="nombre">{t("sucursales.fields.nombre")}</Label>
                <Input id="nombre" placeholder="Sucursal Principal" {...register("nombre")} />
                {errors.nombre && (
                  <p className="text-sm text-destructive">{t(errors.nombre.message as string)}</p>
                )}
              </div>
            </div>

            {/* Dirección */}
            <div className="space-y-2">
              <Label htmlFor="direccion">{t("sucursales.fields.direccion")}</Label>
              <Input
                id="direccion"
                placeholder="Av. Principal #123, Ciudad"
                {...register("direccion")}
              />
              {errors.direccion && (
                <p className="text-sm text-destructive">{t(errors.direccion.message as string)}</p>
              )}
            </div>

            {/* Teléfono y Email */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="telefono">{t("sucursales.fields.telefono")}</Label>
                <Input id="telefono" placeholder="+504 2222-3333" {...register("telefono")} />
                {errors.telefono && (
                  <p className="text-sm text-destructive">{t(errors.telefono.message as string)}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t("sucursales.fields.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="sucursal@lab.com"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{t(errors.email.message as string)}</p>
                )}
              </div>
            </div>

            {/* Opera 24 horas */}
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="opera_24_horas">{t("sucursales.fields.opera24Horas")}</Label>
                <p className="text-sm text-muted-foreground">
                  {t("sucursales.fields.opera24HorasDesc")}
                </p>
              </div>
              <Switch
                id="opera_24_horas"
                checked={opera24Horas}
                onCheckedChange={(checked) => setValue("opera_24_horas", checked)}
              />
            </div>

            {/* Horarios (solo si no opera 24h) */}
            {!opera24Horas && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="horario_apertura">{t("sucursales.fields.horarioApertura")}</Label>
                  <Input id="horario_apertura" type="time" {...register("horario_apertura")} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="horario_cierre">{t("sucursales.fields.horarioCierre")}</Label>
                  <Input id="horario_cierre" type="time" {...register("horario_cierre")} />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
